import { type JSX, type ParentProps, createEffect, createMemo, createSignal, onCleanup, onMount } from "solid-js";
import { config } from "~/config";
import { createLocalStorage } from "~/primitives/localStorage";
import { useDeeplink } from "~/providers/DeeplinkProvider/context";
import { isAuthenticated } from "~/stores/auth";
import { getDocument } from "../repository/document";
import type { Article, Asset, MoreInfo, Section } from "../types";
import {
	getArticleParam,
	isDocumentLink,
	isMeerInfoLink,
	matchSearch,
	setArticleParam,
} from "../widgets/document/helpers";
import { DocumentContext } from "./context";

export interface DocumentProviderProps {
	id: string;
	"protect-moreinfo"?: boolean;
}

export function DocumentProvider(props: ParentProps<DocumentProviderProps>) {
	const { routeToDeeplinkOrUrl } = useDeeplink();

	const [documentTitle, setDocumentTitle] = createSignal<string>("");
	const [buildNumber, setBuildNumber] = createSignal(0);
	const [keywords, setKeywords] = createSignal<string[][]>([]);
	const [articles, setArticles] = createSignal<Article[]>([]);
	const [assets, setAssets] = createSignal<Asset[]>([]);
	const [moreInfo, setMoreInfo] = createSignal<MoreInfo[]>([]);
	const [selected, setSelected] = createSignal<Article>();
	const [clickedArticle, setClickedArticle] = createSignal<Article>();
	const [selectedMoreInfo, setSelectedMoreInfo] = createSignal<MoreInfo>();
	const [tab, setTab] = createSignal<string>("toc");

	// Filter functionality
	const [search, setSearch] = createSignal<string>("");
	const [searches, setSearches] = createSignal<string[]>([]);
	const [searchAmount, setSearchAmount] = createSignal<number>(0);

	// Mobile functionality
	const [sheetOpen, setSheetOpen] = createSignal<boolean>(false);
	const [scrollPosition, setScrollPosition] = createSignal<number>(0);

	const documentId = createMemo(() => props.id);

	const [bookmarks, setBookmarks] = createLocalStorage<Set<string>>({
		key: `yaa:document-bookmarks-${documentId()}`,
		onSerialize(value) {
			return JSON.stringify(Array.from(value));
		},
		onDeserialize(value) {
			return new Set((value && JSON.parse(value)) || []);
		},
	});

	const onPopState = () => {
		const articleFromRequest = articles().find((article) => article.id === getArticleParam());
		if (articleFromRequest) {
			setSelected(articleFromRequest);
		}
	};

	onMount(async () => {
		window.addEventListener("popstate", onPopState);

		const { document, documentKeywords } = await getDocument(props.id);

		setKeywords(documentKeywords);
		setArticles(document.latestBuild.articles);
		setAssets(document.latestBuild.assets);
		setMoreInfo(document.latestBuild.moreinfo);
		setDocumentTitle(document.shortName);
		setBuildNumber(document.latestBuild.buildNumber);

		const articleFromRequest = document.latestBuild.articles.find((article) => article.id === getArticleParam());
		const firstArticle = document.latestBuild.articles.find((article) => article.isViewable);

		setSelected(articleFromRequest ?? firstArticle);
	});

	onCleanup(() => {
		window.removeEventListener("popstate", onPopState);
	});

	const handleToggleBookmark = () => {
		const books = bookmarks();
		const newBookmarks = new Set(books);
		const article = selected();

		if (!article) {
			return;
		}

		if (books.has(article.id)) {
			newBookmarks.delete(article.id);
		} else {
			newBookmarks.add(article.id);
		}

		setBookmarks(newBookmarks);
	};

	const handleDirectionArticle = (direction: number) => {
		const arts = articles();
		const currentIndex = arts.findIndex((article) => article.id === selected()?.id);
		const newIndex = currentIndex + direction;

		if (newIndex < 0 || newIndex >= arts.length) {
			return;
		}

		const article = arts[newIndex];

		if (!article.isViewable) {
			if (direction > 0) {
				handleDirectionArticle(direction + 1);
			} else {
				handleDirectionArticle(direction - 1);
			}
		} else {
			setSelected(article);
		}
	};

	createEffect(() => {
		setArticleParam(selected()?.id);
	});

	const closeMoreInfo = () => {
		setSelectedMoreInfo(undefined);
	};

	const handleLinkClick = (event: MessageEvent) => {
		const info = moreInfo();
		const arts = articles();

		if (event.data.source !== "document-viewer") {
			return;
		}

		const { href } = event.data;

		if (isDocumentLink(href, props.id)) {
			const articleId = href.replace(new RegExp(`https://${config.deepLinkEndpoint}/articles/${props.id}/`), "");
			const article = arts.find((article) => article.id === articleId);

			if (article) {
				setClickedArticle(article);
				return;
			}
		} else if (isMeerInfoLink(href)) {
			const meerinfoId = href.replace("meerinfo://", "");
			const meerinfo = info.find((info) => info.id === meerinfoId);

			if (meerinfo) {
				setSelectedMoreInfo(meerinfo);
				return;
			}
		}

		routeToDeeplinkOrUrl(href);
	};

	const shouldDisplay = (article: Article) => {
		const searched = searches();

		const matched =
			article.isProtected && !isAuthenticated() && search()
				? false
				: matchSearch(searched, [article.title, article.content, article.tocSubtitle]);

		if (article.isSubHeading) {
			return matched;
		}

		return article.isViewable && matched;
	};

	const sections = createMemo(() => {
		let amount = 0;
		const sections: Section[] = [];
		let currSection: Section | null = null;
		const arts = articles();

		if (arts.length === 0) {
			return sections;
		}

		for (const article of arts) {
			if (article.isHeading) {
				if (currSection && currSection.articles.length > 0) {
					sections.push(currSection);
				}

				currSection = {
					heading: article,
					articles: [],
				};

				if (shouldDisplay(article)) {
					currSection.articles.push(article);
					amount++;
				}
			} else {
				if (!currSection) {
					currSection = { heading: article, articles: [] };
					continue;
				}

				if (shouldDisplay(article)) {
					currSection.articles.push(article);
					amount++;
				}
			}
		}

		setSearchAmount(amount);

		if (currSection && currSection.articles.length > 0) {
			sections.push(currSection);
		}

		return sections;
	});

	const isBookmarked = (article: Article | undefined) => {
		return article ? bookmarks().has(article.id) : false;
	};

	const isProtected = createMemo(() => {
		const article = selected();
		return article ? article.isProtected : false;
	});

	const onSearch: JSX.EventHandlerUnion<HTMLInputElement, InputEvent> = (event) => {
		const words = keywords().map((group) => group.map((word) => word.toLowerCase()));
		let value = event.currentTarget.value.trim();

		// We have to set the search value first,
		// because the search function is called first, in order
		// to have non-case sensitive search results.

		setSearch(value);
		value = value.toLowerCase();

		if (value === "") {
			setSearches([value]);
			return;
		}

		for (const group of words) {
			if (group.includes(value)) {
				setSearches(group);
				return;
			}
		}

		setSearches([value]);
	};

	const filteredArticle = createMemo(() => {
		const searched = searches().map((search) => search.toLowerCase());
		const arts = articles();

		if (searched.length === 0) {
			return arts;
		}

		return arts.filter((article) => {
			return matchSearch(searched, [article.title, article.content, article.tocSubtitle]);
		});
	});

	return (
		<DocumentContext.Provider
			value={{
				buildNumber,
				articles: filteredArticle,
				selected,
				clickedArticle,
				keywords,
				assets,
				moreInfo,
				searches,
				documentTitle,
				bookmarks,
				selectedMoreInfo,
				sections,
				tab,
				searchAmount,

				setSelected,
				setClickedArticle,
				setMoreInfo,
				setSearches,
				setDocumentTitle,
				setTab,

				search,
				onSearch,

				handleLinkClick,
				handleToggleBookmark,
				handleDirectionArticle,
				closeMoreInfo,
				isBookmarked,
				isProtected,

				sheetOpen,
				setSheetOpen,
				scrollPosition,
				setScrollPosition,

				documentId,
			}}
		>
			{props.children}
		</DocumentContext.Provider>
	);
}
